import React, { useEffect, useState, useCallback, useRef } from 'react';
import type { TablePaginationConfig } from 'antd';
import { Form, Input, Button, Table, Space, Tooltip, message, Pagination } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { examination } from '@/api/examinationApi';
import moment from 'moment';

import HrSelect from '@/components/hr-select';
import { useHistory } from 'react-router-dom';
import type { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { removeLeadingAndTrailingSpaces } from "@/utils/index";

const columns: ColumnType<API.question>[] = [
  {
    title: '候选人姓名',
    dataIndex: ['candidate', 'name'],
  },
  {
    title: '候选人邮箱',
    dataIndex: ['candidate', 'email'],
  },
  {
    title: '候选人手机号',
    dataIndex: ['candidate', 'phone'],
  },
  {
    title: '考卷名',
    dataIndex: ['exam', 'type'],
  },
  {
    title: '考试分数',
    dataIndex: ['exam', 'score'],
    sorter: true,
  },
  {
    title: '选择题分数',
    dataIndex: ['exam', 'choiceScore'],
  },
  {
    title: '编程题分数',
    dataIndex: ['exam', 'codeScore'],
  },
  {
    title: '答题用时',
    dataIndex: ['exam', 'usedTime'],
    /** 渲染函数 */
    render(v) {
      return v ? parseInt(String(v / 1000 / 60), 10)  + '分钟' : '';
    },
  },
  {
    title: '答题完成时间',
    dataIndex: ['exam', 'submitTime'],
    sorter: true,
    // sortOrder:'descend',
    /** 处理渲染结果 */
    render(v) {
      return v ? moment(v).format('YYYY-MM-DD HH:mm:ss') : '';
    },
  },
  // {
  //   title: '是否作弊',
  //   dataIndex: ['exam', 'cheat'],
  //   /** 处理渲染结果 */
  //   render(v) {
  //     return v ? '是' : '否';
  //   },
  // },
  {
    title: '作弊情况',
    dataIndex: ['exam', 'cheatDetails'],
  },
  {
    title: 'hr',
    dataIndex: 'hr',
    /** 处理渲染结果 */
    render(v: {id: string; name: string}[]) {
      return v.map(item => item.name).join('、');
    },
  },
  {
    title: '操作',
    /** 处理渲染结果 */
    render(v: API.question) {
      return <Button type="link" onClick={() => {
        window.open(`/examinee_answers?exam_id=${v.exam.id}&candidate_id=${v.candidate.id}&score=${v.exam.score}`);
      }}>查看</Button>;
    },
  },
];

/** 结果列表 */
const ResultList = () => {
  const history = useHistory();
  const [list, setList] = useState<API.question[]>([]);
  const [pagination, setPagination] = useState<API.pagination>({
    page: 1,
    sinceId: 0,
    maxId: 0,
    limit: 10,
    total: 0
  });
  const sortRef = useRef<{
    type: string,
    sort: string
  }>({
    type: 'time',
    sort: 'desc'
  });
  const [form] = Form.useForm<{candidateName?: string, hrName?: string[]}>();

  /** 获取考试数据 */
  const getData = useCallback(async (page: number, candidateName?: string, hrName?: string) => {
    try {
      const ret = await examination.list({
        limit: pagination.limit,
        sinceId: pagination.limit * page + 1,
        candidateName: candidateName,
        hrName: hrName,
        type: sortRef.current.type,
        sort: sortRef.current.sort
      });
      if(ret.code === 0) {
        setList(ret.data.result);
        setPagination(ret.data.pagination);
      } else {
        message.error(ret.message);
      }
    } catch (e) {
      //
    }
  }, [pagination]);

  const handleSearch = useCallback(() => {
    const formValue = form.getFieldsValue();
    getData(0, removeLeadingAndTrailingSpaces(formValue.candidateName) || undefined, formValue.hrName?.join(',') || undefined);
  }, [form, getData]);

  useEffect(() => {
    /** 获取数据 */
    getData(0);
  }, []);

  const handleReset = useCallback(() => {
    form.resetFields();
    getData(0);
  }, []);

  const tableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<API.question> | SorterResult<API.question>[]) => {
    if(!Array.isArray(sorter) && sorter.field) {
      const obj = {
        ascend: 'asc',
        descend: 'desc',
        submitTime: 'time',
        score: 'score'
      };
      sortRef.current = ({
        type: obj[sorter.field[1] || 'time'],
        sort: obj[sorter.order || 'ascend'],
      });
    }
    const formValue = form.getFieldsValue();
    getData(pagination.current! - 1, formValue.candidateName, formValue.hrName?.join(','));
  }, [getData, form]);

  return (
    <div
      style={{
        width: '80%',
        margin: '20px auto 50px',
      }}
    >
      <div style={{ display: 'flex', marginBottom: '50px' }}>
        <h2 style={{ flex: 1 }}>候选人考核列表</h2>
        <Button
          type="primary"
          onClick={() => {
            history.push('/create_exam');
          }}
        >
          创建试卷
        </Button>
        <Button
          style={{marginLeft: '20px'}}
          type="primary"
          onClick={() => {
            history.push('/create_exam_lark');
          }}
        >
          创建试卷 With Lark
        </Button>
      </div>
      <Form form={form} layout="inline" style={{ marginBottom: 20 }} autoComplete="off">
        <Form.Item name="candidateName" label="候选人姓名">
          <Input />
        </Form.Item>
        <Form.Item name="hrName" label="HR">
          <HrSelect
            mode="multiple"
            placeholder="请选择hr"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={handleSearch}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
            <Tooltip placement="topRight" title="如果有新数据添加，点我更新">
              <Button onClick={() => window.location.reload()}>刷新</Button>
            </Tooltip>
          </Space>
        </Form.Item>
      </Form>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        onChange={tableChange}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          pageSizeOptions: ['10', '20', '30'],
        }}
      />
    </div>
  );
};

export default ResultList;
